import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { useIsIOSApp } from '@/hooks/useIsIOSApp';
import { Label } from '../components/ui/label';
import { CurrencyInput } from '../components/ui/currency-input';
import { useAuth } from '../App';
import { toast } from 'sonner';
import {
  ShoppingBag,
  ChevronLeft,
  Loader2,
  Upload,
  X,
  MapPin,
  DollarSign,
  Tag,
  Mail,
  Phone,
  MessageCircle
} from 'lucide-react';
import { getCurrencyForCountry } from '../utils/currencyMapping';

const API = ''; // Use relative URL for same-origin API calls

const CreateListingPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { listingId } = useParams();
  const isEditMode = !!listingId;
  const isIOSApp = useIsIOSApp();
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState({
    categories: [
      'Electronics', 'Furniture', 'Clothing', 'Books', 'Sports', 'Toys',
      'Vehicles', 'Home & Garden', 'Musical Instruments', 'Art & Collectibles',
      'Baby & Kids', 'Kitchen & Dining', 'Other'
    ],
    conditions: ['New', 'Like New', 'Good', 'Fair', 'For Parts']
  });
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: '',
    condition: 'Good',
    price: '',
    price_currency: 'USD',
    is_negotiable: true,
    is_free: false,
    images: [],
    city: '',
    country: '',
    district: '',
    contact_name: '',
    contact_email: '',
    contact_phone: '',
    contact_whatsapp: '',
    prefer_messages: true
  });

  useEffect(() => {
    if (!user) {
      toast.error('Please sign in to create a listing');
      navigate('/');
      return;
    }
    fetchCategories();
    if (isEditMode) {
      fetchListingData();
    }
  }, [user, navigate]);

  const fetchListingData = async () => {
    try {
      const response = await axios.get(`${API}/api/marketplace/${listingId}`);
      const l = response.data;
      setFormData({
        title: l.title || '',
        description: l.description || '',
        category: l.category || '',
        condition: l.condition || 'Good',
        price: l.price || '',
        price_currency: l.price_currency || 'USD',
        is_negotiable: l.is_negotiable ?? true,
        is_free: l.is_free || false,
        images: l.images || [],
        city: l.city || '',
        country: l.country || '',
        district: l.district || '',
        contact_name: l.contact_name || '',
        contact_email: l.contact_email || '',
        contact_phone: l.contact_phone || '',
        contact_whatsapp: l.contact_whatsapp || '',
        prefer_messages: l.prefer_messages ?? true
      });
    } catch (error) {
      toast.error('Failed to load listing data');
      navigate('/marketplace');
    }
  };

  // Auto-select currency based on country
  useEffect(() => {
    if (formData.country) {
      const currency = getCurrencyForCountry(formData.country);
      if (currency) {
        setFormData(prev => ({ ...prev, price_currency: currency }));
      }
    }
  }, [formData.country]);

  const fetchCategories = async () => {
    try {
      const res = await axios.get(`${API}/api/marketplace/categories`);
      setCategories(res.data);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  };

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    if (formData.images.length + files.length > 8) {
      toast.error('Maximum 8 images allowed');
      return;
    }

    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = (event) => {
        setFormData(prev => ({
          ...prev,
          images: [...prev.images, event.target.result]
        }));
      };
      reader.readAsDataURL(file);
    });
  };

  const removeImage = (index) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title || !formData.description || !formData.category || !formData.city || !formData.country) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (!formData.is_free && !formData.price) {
      toast.error('Please enter a price or mark as free');
      return;
    }

    setLoading(true);
    try {
      const submitData = {
        ...formData,
        price: formData.is_free ? 0 : parseFloat(formData.price)
      };

      if (isEditMode) {
        await axios.put(`${API}/api/marketplace/${listingId}`, submitData);
        toast.success('Listing updated!');
        navigate(`/marketplace/${listingId}`);
      } else {
        await axios.post(`${API}/api/marketplace`, submitData);
        toast.success('Listing created successfully! It will be visible after admin approval.');
        navigate('/marketplace');
      }
    } catch (error) {
      // Handle Pydantic validation errors (422) which return an array
      const detail = error.response?.data?.detail;
      let errorMessage = 'Failed to create listing';
      
      if (Array.isArray(detail)) {
        errorMessage = detail[0]?.msg || detail[0]?.message || errorMessage;
      } else if (typeof detail === 'string') {
        errorMessage = detail;
      } else if (typeof detail === 'object' && detail?.msg) {
        errorMessage = detail.msg;
      }
      
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (!user) return null;

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-background py-8">
        <div className="container mx-auto px-4 max-w-3xl">
          {/* Header */}
          <div className="mb-8">
            <button
              onClick={() => navigate('/marketplace')}
              className="inline-flex items-center gap-2 text-muted-foreground hover:text-primary mb-4"
            >
              <ChevronLeft className="w-4 h-4" />
              Back to Marketplace
            </button>
            <h1 className="text-3xl font-black">{isEditMode ? 'Edit Listing' : 'Sell Something'}</h1>
            <p className="text-muted-foreground">{isEditMode ? 'Update your listing details' : 'Create a listing to sell or give away items'}</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Images */}
            <div className="bg-white rounded-2xl border-2 border-border p-6">
              <h2 className="text-xl font-bold mb-4">Photos</h2>
              <div className="grid grid-cols-4 gap-4">
                {formData.images.map((img, idx) => (
                  <div key={idx} className="relative group aspect-square">
                    <img src={img} alt="" className="w-full h-full object-cover rounded-lg" />
                    <button
                      type="button"
                      onClick={() => removeImage(idx)}
                      className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <X className="w-4 h-4" />
                    </button>
                    {idx === 0 && (
                      <span className="absolute bottom-2 left-2 px-2 py-1 bg-black/70 text-white text-xs rounded">
                        Cover
                      </span>
                    )}
                  </div>
                ))}
                {formData.images.length < 8 && (
                  <label className="border-2 border-dashed border-border rounded-lg aspect-square flex flex-col items-center justify-center cursor-pointer hover:border-primary transition-colors">
                    <Upload className="w-8 h-8 text-muted-foreground mb-2" />
                    <span className="text-sm text-muted-foreground">Add Photo</span>
                    <input
                      type="file"
                      accept={isIOSApp ? ".jpg,.jpeg,.png,.gif,.webp,.heic" : "image/*"}
                      multiple
                      onChange={handleImageUpload}
                      className="hidden"
                    />
                  </label>
                )}
              </div>
              <p className="text-sm text-muted-foreground mt-2">Add up to 8 photos. First photo will be the cover.</p>
            </div>

            {/* Basic Info */}
            <div className="bg-white rounded-2xl border-2 border-border p-6">
              <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
                <Tag className="w-5 h-5 text-primary" />
                Item Details
              </h2>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="title">Title *</Label>
                  <Input
                    id="title"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="e.g., IKEA Sofa - Excellent Condition"
                    className="mt-1"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="description">Description *</Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Describe your item in detail - size, color, any defects, reason for selling..."
                    className="mt-1 min-h-[120px]"
                    required
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Category *</Label>
                    <Select
                      value={formData.category}
                      onValueChange={(value) => setFormData({ ...formData, category: value })}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue placeholder="Select category" />
                      </SelectTrigger>
                      <SelectContent>
                        {categories.categories?.map(cat => (
                          <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Condition *</Label>
                    <Select
                      value={formData.condition}
                      onValueChange={(value) => setFormData({ ...formData, condition: value })}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {categories.conditions?.map(cond => (
                          <SelectItem key={cond} value={cond}>{cond}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
            </div>
            </div>

            {/* Location */}
            <div className="bg-white rounded-2xl border-2 border-border p-6">
              <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
                <MapPin className="w-5 h-5 text-primary" />
                Location
              </h2>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="district">District / Neighborhood</Label>
                  <Input
                    id="district"
                    value={formData.district}
                    onChange={(e) => setFormData({ ...formData, district: e.target.value })}
                    placeholder="e.g., Shibuya, Marina Bay"
                    className="mt-1"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="city">City *</Label>
                    <Input
                      id="city"
                      value={formData.city}
                      onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                      placeholder="e.g., Tokyo"
                      className="mt-1"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="country">Country *</Label>
                    <Input
                      id="country"
                      value={formData.country}
                      onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                      placeholder="e.g., Japan"
                      className="mt-1"
                      required
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Pricing */}
            <div className="bg-white rounded-2xl border-2 border-border p-6">
              <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-primary" />
                Pricing
              </h2>
              <div className="space-y-4">
                <div className="flex items-center gap-3 p-4 border rounded-xl">
                  <Checkbox
                    id="is_free"
                    checked={formData.is_free}
                    onCheckedChange={(checked) => setFormData({ ...formData, is_free: checked, price: checked ? '0' : formData.price })}
                  />
                  <Label htmlFor="is_free" className="cursor-pointer">
                    <div className="font-medium">Give it away for free</div>
                    <div className="text-sm text-muted-foreground">No payment required</div>
                  </Label>
                </div>

                {!formData.is_free && (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="price">Price *</Label>
                      <Input
                        id="price"
                        type="number"
                        min="0"
                        value={formData.price}
                        onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                        placeholder="0"
                        className="mt-1"
                        required={!formData.is_free}
                      />
                    </div>
                    <div>
                      <Label>Currency</Label>
                      <CurrencyInput
                        value={formData.price_currency}
                        onChange={(value) => setFormData({ ...formData, price_currency: value })}
                        className="mt-1"
                      />
                    </div>
                  </div>
                )}

                {!formData.is_free && (
                  <div className="flex items-center gap-3">
                    <Checkbox
                      id="is_negotiable"
                      checked={formData.is_negotiable}
                      onCheckedChange={(checked) => setFormData({ ...formData, is_negotiable: checked })}
                    />
                    <Label htmlFor="is_negotiable" className="cursor-pointer">Price is negotiable (OBO)</Label>
                  </div>
                )}
              </div>
            </div>

            {/* Contact Info */}
            <div className="bg-white rounded-2xl border-2 border-border p-6">
              <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
                <Mail className="w-5 h-5 text-primary" />
                Contact Information
              </h2>
              <div className="space-y-4">
                <div className="flex items-center gap-3 p-4 border rounded-xl">
                  <Checkbox
                    id="prefer_messages"
                    checked={formData.prefer_messages}
                    onCheckedChange={(checked) => setFormData({ ...formData, prefer_messages: checked })}
                  />
                  <Label htmlFor="prefer_messages" className="cursor-pointer">
                    <div className="font-medium">Allow in-app messages</div>
                    <div className="text-sm text-muted-foreground">Buyers can message you through ExpatsSphere</div>
                  </Label>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="contact_name">Contact Name</Label>
                    <Input
                      id="contact_name"
                      value={formData.contact_name}
                      onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
                      placeholder="Your name"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="contact_email">Email</Label>
                    <Input
                      id="contact_email"
                      type="email"
                      value={formData.contact_email}
                      onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                      placeholder="your@email.com"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="contact_phone">Phone</Label>
                    <Input
                      id="contact_phone"
                      value={formData.contact_phone}
                      onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
                      placeholder="+1 234 567 8900"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="contact_whatsapp">WhatsApp</Label>
                    <Input
                      id="contact_whatsapp"
                      value={formData.contact_whatsapp}
                      onChange={(e) => setFormData({ ...formData, contact_whatsapp: e.target.value })}
                      placeholder="+1 234 567 8900"
                      className="mt-1"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Submit */}
            <div className="flex justify-end gap-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/marketplace')}
                className="rounded-xl"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={loading}
                className="font-bold rounded-xl px-8"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {isEditMode ? 'Saving...' : 'Posting...'}
                  </>
                ) : (
                  isEditMode ? 'Save Changes' : 'Post Listing'
                )}
              </Button>
            </div>
          </form>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default CreateListingPage;
